import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, type ReactNode } from "react";
import type { IsaacEvaluationScenarioReq } from "@/api/training";
import { listAllTrainingEvalScenarios } from "@/api/trainingEval";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { HatDataTable } from "@/components/table/HatDataTable";
import monoStyles from "@/components/table/dataTableMono.module.css";
import { useAsyncList } from "../useAsyncList";
import {
  ScheduleRecipeScenarioChildRow,
  ScheduleRecipeScenarioRow,
} from "./ScheduleRecipeScenarioTableRow";
import styles from "./ScheduleRecipeScenarioTable.module.css";

const EMPTY = "—";

// 컬럼 순서(1 이름, 2 Scenario ID, 3 Seed)는 module.css 의 하위 행 트랙 번호와 연결된다. 바꾸면
// 거기도 같이 고친다.
const COLUMNS: readonly DataTableColumn<ScheduleRecipeScenarioRow>[] = [
  { key: "name", header: "이름", width: "minmax(200px, 1fr)", render: (row) => row.name },
  {
    key: "scenarioId",
    header: "Scenario ID",
    width: "minmax(200px, 1fr)",
    render: (row) => <span className={monoStyles.mono}>{row.scenarioId}</span>,
  },
  // seed 0 은 값이고 미지정은 부재다. `|| EMPTY` 로 합치면 0 이 사라진다.
  { key: "seed", header: "Seed", width: "100px", render: (row) => row.seed ?? EMPTY },
];

/**
 * recipe 의 Eval scenario 와, 각 scenario 아래에 카탈로그에서 찾은 parameter 하위 행. 빈 목록
 * 안내는 호출부 섹션이 소유한다. 이 표는 scenario 가 하나 이상일 때만 마운트된다.
 */
export function ScheduleRecipeScenarioTable({
  scenarios,
}: {
  scenarios: readonly IsaacEvaluationScenarioReq[];
}): ReactNode {
  // 카탈로그는 세션 동안 한 번만 받는다(`listAllTrainingEvalScenarios` 캐시). 모듈 함수라 참조가
  // 안정적이므로 마운트당 한 번만 요청한다.
  const { items, status } = useAsyncList(listAllTrainingEvalScenarios);
  const source = useMemo(
    () =>
      new StaticTableSource(
        scenarios.map(
          (scenario, index) => new ScheduleRecipeScenarioRow(scenario, index, { items, status }),
        ),
      ),
    [scenarios, items, status],
  );

  useEffect(() => () => source.dispose(), [source]);

  return (
    <HatDataTable
      source={source}
      columns={COLUMNS}
      ariaLabel="Eval scenario"
      showIndex={false}
      minWidth="0px"
      defaultRowChildrenExpanded
      renderRowChild={(child) =>
        child instanceof ScheduleRecipeScenarioChildRow ? <ScenarioChildLine row={child} /> : null
      }
    />
  );
}

// c6 rowChildCell(표 컬럼 트랙의 subgrid) 안에서 트랙을 이어받는다. 그래서 key 는 이름 열 아래,
// 값은 나머지 열 아래에 놓인다.
function ScenarioChildLine({ row }: { row: ScheduleRecipeScenarioChildRow }): ReactNode {
  const { content } = row;
  if (content.kind === "note") {
    return (
      <span className={styles.line}>
        <span className={styles.note}>{content.text}</span>
      </span>
    );
  }
  return (
    <span className={styles.line}>
      <span className={styles.paramKey}>{content.key}</span>
      <span className={styles.paramValue}>{content.value}</span>
    </span>
  );
}
